/**
 * mobile-menu-merge.js — the MM-2 merge engine (Workstream B, plan D4/D5/D6).
 *
 * PURE: no store access, no transactions, no timers. Callers own the single
 * state write (`section-manager.js` apply/reset paths; `swapSharedSection`
 * folds a re-merge into its existing transaction — never a nested one).
 *
 * `mergeMobileMenu(nav, fragment, cssEngine)` re-dresses an eligible host
 * nav's `.cs-ul-wrapper` drawer with a fragment's chrome + per-link templates
 * while the link list's SEMANTICS stay host-owned: every link is parsed into
 * a full model (label DOM node, href, target/rel/hreflang, ARIA, active +
 * client-link markers, dropdown children) and rebuilt via DOM APIs — no
 * HTML/href string interpolation anywhere. Hrefs come from the host's own
 * state and still pass a scheme allowlist before being set.
 *
 * Reversibility (D5): the FIRST apply captures `stockSnapshot` — the exact
 * pre-apply drawer subtree + drawer CSS slice. Revert restores those bytes;
 * it never re-fetches by catalogKey. The snapshot is carried across A→B
 * fragment swaps.
 *
 * CSS is never regex-parsed. The one platform split lives behind the
 * `cssEngine` seam: `createCssTreeCssEngine(csstree)` (Node — tests, tooling;
 * the css-tree module is INJECTED so this file never imports it and stays
 * browser-loadable) and `createCssomCssEngine()` (browser — constructable
 * CSSStyleSheet). Both drive the same MM-0 predicates
 * (shared/mobile-menu-css-predicate.js), so parser, builder, and tests cannot
 * disagree about what "drawer CSS" means. A rule whose selector list mixes
 * drawer and host targets is SPLIT per selector, never dropped whole.
 */

import { reservedEditableIds } from '../shared/editable-id-repair.js';
import {
  FRAGMENT_CONTRACT_VERSION,
  MARKER_ATTRS,
  ACTIVATION_CONTRACT,
  checkNavEligibility
} from '../shared/mobile-menu-contract.js';
import {
  collectDrawerClassSet,
  partitionSelectors,
  selectorTargetsFragmentMarkers,
  parseMaxWidthEdge,
  sameEdge,
  detectMobileEdge
} from '../shared/mobile-menu-css-predicate.js';
import { splitSelectors } from '../shared/css-scoper.js';
import { parseClientLinkMarker, CLIENT_LINK_MARKER_PREFIX } from '../shared/client-info.js';
import { stampEditableIds, pruneOrphanOverrides } from '../editing/editable-utils.js';
import {
  deriveContrastOverrides,
  createCssTreeRuleReader,
  createCssomRuleReader
} from '../shared/mobile-menu-contrast.js';

/** History labels — imported by shell.js (full-render list) and callers. */
export const MOBILE_MENU_LABELS = Object.freeze({
  apply: 'Apply mobile menu',
  reset: 'Reset mobile menu'
});

const SAFE_HREF_SCHEMES = new Set(['http', 'https', 'mailto', 'tel', 'sms']);

/** Scheme allowlist for hrefs re-set through the merge (D4 sanitization). */
export function safeHref(href) {
  const value = String(href || '').trim();
  if (!value) return '#';
  if (value.startsWith(CLIENT_LINK_MARKER_PREFIX)) return value;
  const scheme = value.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  if (!scheme) return value; // relative path, hash, or query — no scheme to abuse
  return SAFE_HREF_SCHEMES.has(scheme[1].toLowerCase()) ? value : '#';
}

function parseHTMLFragment(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  return { container: doc.body.firstElementChild, doc };
}

/**
 * Neutralize DONOR contact data carried in fragment chrome.
 *
 * The link LIST is rebuilt from the host, but chrome (`[data-pb-mm-chrome]` —
 * drawer CTAs, footers, contact rows) is cloned verbatim from the source
 * template, hrefs included. 4 of the 10 corpus fragments ship a real business's
 * contact details that way: casablanca `tel:6822483365`, shatteredvision
 * `tel:719-377-2282`, wholesalewindows `tel:+18178747218` +
 * `mailto:tom@hginstall.com`, mosaic a guestybookings booking URL. Applying one
 * to a client site published another business's phone number on a "Call Us"
 * button — and the link checker would not stop it, because external links are
 * warnings, not errors.
 *
 * Contact schemes become the client-binding markers the builder already
 * resolves per project; absolute donor URLs are neutralized to '#' unless they
 * are a bare root, which binds to the client domain. Host-relative paths and
 * fragments are left alone: they are not donor data, and a path the host lacks
 * is an ordinary internal-link finding the checker DOES report as an error.
 *
 * Rebinding the href alone is NOT enough — the same details are rendered as
 * VISIBLE TEXT (shatteredvision prints "719-377-2282" inside the call button,
 * wholesalewindows puts "(817) 874-7218" and "tom@hginstall.com" in
 * `.ww-contact-value` spans) and named in `aria-label`s ("Call Shattered Vision
 * Auto Glass"). Text is bound through the same `data-pb-client` markers the
 * client-info pass already resolves, and donor-named labels are replaced with
 * neutral copy. This is structural, not profile-matched, which matters:
 * casablanca's source profile is a stub with `phoneVariants: []`, so nothing
 * that recognizes donor values by profile would catch its `tel:6822483365`.
 */
const EMAIL_TEXT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_TEXT_RE = /^[+()\d][\d\s()+.-]{5,}$/;

/** Deepest element inside `anchor` whose entire text is the contact value. */
function contactTextTarget(anchor, shape) {
  const candidates = [...anchor.querySelectorAll('*'), anchor];
  let best = null;
  for (const el of candidates) {
    if (el.children.length) continue; // leaf text holders only
    const text = (el.textContent || '').trim();
    if (!text || !shape.test(text)) continue;
    best = el;
    break;
  }
  if (best) return best;
  const own = (anchor.textContent || '').trim();
  return shape.test(own) ? anchor : null;
}

function bindContactText(anchor, key) {
  const shape = key === 'email' ? EMAIL_TEXT_RE : PHONE_TEXT_RE;
  const target = contactTextTarget(anchor, shape);
  if (!target) return false;
  const textKey =
    key === 'email'
      ? 'email'
      : /^\+?\d+$/.test((target.textContent || '').trim())
        ? 'phone'
        : 'phoneFormatted';
  if (target === anchor) anchor.setAttribute('data-pb-client-text', textKey);
  else target.setAttribute('data-pb-client', textKey);
  return true;
}

const NEUTRAL_LABEL = { phone: 'Call us', email: 'Email us' };

function sanitizeChromeLinks(root) {
  let rewritten = 0;
  const chromeRoots = [
    ...(root.matches?.(`[${MARKER_ATTRS.chrome}]`) ? [root] : []),
    ...root.querySelectorAll(`[${MARKER_ATTRS.chrome}]`)
  ];
  for (const chrome of chromeRoots) {
    for (const anchor of chrome.querySelectorAll('a[href]')) {
      const href = String(anchor.getAttribute('href') || '').trim();
      if (!href || href.startsWith(CLIENT_LINK_MARKER_PREFIX)) continue;
      let next = null;
      let contactKey = null;
      if (/^tel:/i.test(href)) {
        next = `${CLIENT_LINK_MARKER_PREFIX}phone`;
        contactKey = 'phone';
      } else if (/^mailto:/i.test(href)) {
        next = `${CLIENT_LINK_MARKER_PREFIX}email`;
        contactKey = 'email';
      } else if (/^https?:\/\//i.test(href)) {
        next = /^https?:\/\/[^/]+\/?$/i.test(href) ? `${CLIENT_LINK_MARKER_PREFIX}domain` : '#';
      } else {
        const safe = safeHref(href);
        if (safe !== href) next = safe;
      }
      if (next && next !== href) {
        anchor.setAttribute('href', next);
        rewritten += 1;
      }
      if (contactKey) {
        bindContactText(anchor, contactKey);
        // The donor's business name rides along in the accessible name.
        if (anchor.hasAttribute('aria-label')) {
          anchor.setAttribute('aria-label', NEUTRAL_LABEL[contactKey]);
        }
      }
    }
  }
  return rewritten;
}

// ── link model (D4) ──────────────────────────────────────────────────────

// Functional dropdown wiring is regenerated at render, never copied as label
// ARIA; stock CodeStitch chevrons are host decoration a template replaces.
const NON_LABEL_ARIA = new Set(['aria-expanded', 'aria-controls']);
const STOCK_DECO_CLASSES = ['cs-drop-icon'];

function isLabelNode(node) {
  if (node.nodeType !== 1) return true; // text/comment nodes are label content
  if (node.hasAttribute(MARKER_ATTRS.deco)) return false;
  if (node.tagName === 'UL') return false;
  if (STOCK_DECO_CLASSES.some((cls) => node.classList?.contains(cls))) return false;
  return true;
}

function modelItemFromLi(li) {
  const link = li.querySelector('.cs-li-link');
  const subUl = li.querySelector('ul');
  const isDropdown = li.classList.contains(ACTIVATION_CONTRACT.dropdownClass) || !!subUl;
  const item = {
    kind: isDropdown ? 'dropdown-trigger' : 'link',
    hasLink: !!link,
    liClone: li.cloneNode(true),
    labelNodes: [],
    href: null,
    target: null,
    rel: null,
    hreflang: null,
    ariaAttrs: {},
    active: false,
    clientLinkKey: '',
    children: [],
    submenuId: subUl?.id || null
  };
  if (link) {
    item.href = link.getAttribute('href');
    item.target = link.getAttribute('target');
    item.rel = link.getAttribute('rel');
    item.hreflang = link.getAttribute('hreflang');
    for (const attr of [...link.attributes]) {
      if (attr.name.startsWith('aria-') && !NON_LABEL_ARIA.has(attr.name)) {
        item.ariaAttrs[attr.name] = attr.value;
      }
    }
    item.active =
      link.classList.contains(ACTIVATION_CONTRACT.rootActiveClass) ||
      li.classList.contains(ACTIVATION_CONTRACT.rootActiveClass);
    item.clientLinkKey = parseClientLinkMarker(item.href || '');
    // A fragment-dressed host keeps its label content inside the label slot;
    // a stock host's label is the link's own children. Nested spans are label
    // content and MUST survive (the textContent-destruction regression).
    const labelHost = link.querySelector(`[${MARKER_ATTRS.label}]`) || link;
    for (const node of [...labelHost.childNodes]) {
      if (isLabelNode(node)) item.labelNodes.push(node.cloneNode(true));
    }
  }
  if (subUl) {
    for (const subLi of [...subUl.children]) {
      if (subLi.tagName === 'LI') item.children.push(subLi.cloneNode(true));
    }
  }
  return item;
}

/** Parse the host's `ul.cs-ul` into the full per-link model. */
export function buildNavLinkModel(listEl) {
  const items = [];
  for (const li of [...listEl.children]) {
    if (li.tagName === 'LI') items.push(modelItemFromLi(li));
  }
  return { items };
}

// ── list rendering (D4) ──────────────────────────────────────────────────

function replaceIndexTokens(li, index) {
  const padded = String(index).padStart(2, '0');
  for (const el of [li, ...li.querySelectorAll('*')]) {
    for (const attr of [...el.attributes]) {
      if (attr.value.includes('{{index}}') || attr.value.includes('{{indexPadded}}')) {
        el.setAttribute(
          attr.name,
          attr.value.replaceAll('{{index}}', String(index)).replaceAll('{{indexPadded}}', padded)
        );
      }
    }
    // Numeric decorations (mosaic's 01/02/03 rail) renumber per item — the
    // template was derived from the source's FIRST item, so its literal
    // digits would otherwise repeat on every link.
    if (
      el.hasAttribute(MARKER_ATTRS.deco) &&
      el.children.length === 0 &&
      /^\d{1,3}$/.test(el.textContent.trim())
    ) {
      el.textContent = padded;
    }
  }
}

function setOrRemoveAttr(el, name, value) {
  if (value != null && value !== '') el.setAttribute(name, value);
  else el.removeAttribute(name);
}

function uniqueSubmenuId(preferred, used, index) {
  let candidate = preferred || `pb-mm-drop-${index + 1}`;
  while (used.has(candidate)) candidate = `${candidate}-2`;
  used.add(candidate);
  return candidate;
}

function renderTemplatedItem(item, index, templateHtml, doc, usedSubmenuIds) {
  const holder = doc.createElement('div');
  holder.innerHTML = templateHtml;
  const li = holder.firstElementChild;
  if (!li) return item.liClone.cloneNode(true);
  replaceIndexTokens(li, index);

  const link = li.querySelector('.cs-li-link');
  if (link) {
    if (item.href != null) {
      link.setAttribute('href', safeHref(item.href));
    } else if (link.getAttribute('href') === '{{href}}') {
      link.removeAttribute('href');
    }
    setOrRemoveAttr(link, 'target', item.target);
    setOrRemoveAttr(link, 'rel', item.rel);
    setOrRemoveAttr(link, 'hreflang', item.hreflang);
    // Template ARIA snapshotted from the source's own links is stale for this
    // host — drop label ARIA, then re-apply the host's.
    for (const attr of [...link.attributes]) {
      if (attr.name.startsWith('aria-') && !NON_LABEL_ARIA.has(attr.name)) {
        link.removeAttribute(attr.name);
      }
    }
    for (const [name, value] of Object.entries(item.ariaAttrs)) {
      link.setAttribute(name, value);
    }
    link.classList.toggle(ACTIVATION_CONTRACT.rootActiveClass, item.active);
    if (!link.classList.length) link.removeAttribute('class');
  }

  const labelSlot = li.querySelector(`[${MARKER_ATTRS.label}]`) || link;
  if (labelSlot) {
    if (labelSlot.hasAttribute(MARKER_ATTRS.label)) labelSlot.textContent = '';
    for (const node of item.labelNodes) labelSlot.appendChild(node.cloneNode(true));
  }

  if (item.kind === 'dropdown-trigger') {
    const submenuSlot = li.querySelector(`[${MARKER_ATTRS.slot}="submenu"]`);
    const submenuId = uniqueSubmenuId(item.submenuId, usedSubmenuIds, index - 1);
    if (submenuSlot) {
      submenuSlot.removeAttribute(MARKER_ATTRS.slot);
      submenuSlot.id = submenuId;
      for (const subLi of item.children) submenuSlot.appendChild(subLi.cloneNode(true));
    }
    const trigger = link || li.querySelector('[aria-controls]');
    if (trigger && submenuSlot) {
      trigger.setAttribute('aria-controls', submenuId);
      trigger.setAttribute('aria-expanded', 'false');
    }
  }
  return li;
}

/**
 * Build the fragment-dressed link list. The host list's own element shell
 * (class, id, aria) is kept — it is the shared desktop list — and each item
 * is either re-templated (full model rebuild) or, when the fragment declares
 * no template for its kind, carried verbatim.
 */
export function renderMenuList(hostListEl, model, fragment, doc) {
  const newList = hostListEl.cloneNode(false);
  const usedSubmenuIds = new Set();
  model.items.forEach((item, idx) => {
    let templateHtml =
      item.kind === 'dropdown-trigger'
        ? fragment.dropdownTemplate || null
        : fragment.itemTemplate || null;
    let appendHostSubmenu = false;
    // MM-5: a fragment with an item template but no dropdown template (its
    // source drawer simply has no dropdown — casablanca) used to fall back to
    // a verbatim host item, one visibly-stock row among templated siblings.
    // Render the TRIGGER with the item template and carry the host's own
    // submenu across verbatim instead.
    if (item.kind === 'dropdown-trigger' && !templateHtml && fragment.itemTemplate) {
      templateHtml = fragment.itemTemplate;
      appendHostSubmenu = true;
    }
    if (!templateHtml || !item.hasLink) {
      newList.appendChild(item.liClone.cloneNode(true));
      return;
    }
    const li = renderTemplatedItem(item, idx + 1, templateHtml, doc, usedSubmenuIds);
    if (appendHostSubmenu && item.children.length) {
      const submenu = doc.createElement('ul');
      submenu.className = 'cs-drop-ul';
      submenu.id = uniqueSubmenuId(item.submenuId, usedSubmenuIds, idx);
      for (const subLi of item.children) submenu.appendChild(subLi.cloneNode(true));
      li.classList.add(ACTIVATION_CONTRACT.dropdownClass);
      li.appendChild(submenu);
      const trigger = li.querySelector('.cs-li-link');
      if (trigger) {
        trigger.setAttribute('aria-controls', submenu.id);
        trigger.setAttribute('aria-expanded', 'false');
      }
    }
    newList.appendChild(li);
  });
  return newList;
}

// ── shared CSS-engine helpers ────────────────────────────────────────────

const ANIMATION_NAME_RE = /animation(?:-name)?\s*:\s*([a-zA-Z_][\w-]*)/g;

function animationNames(cssText) {
  const names = new Set();
  let match;
  ANIMATION_NAME_RE.lastIndex = 0;
  while ((match = ANIMATION_NAME_RE.exec(cssText)) !== null) names.add(match[1]);
  return names;
}

function toggleShownIn(selectors, bodyText) {
  return (
    selectors.some((s) => s.includes(`.${ACTIVATION_CONTRACT.toggleClass}`)) &&
    /display\s*:\s*(?!none)[a-z-]/.test(bodyText)
  );
}

/**
 * Shared partition assembler: both engines reduce their walk to per-media
 * groups of { selectors, body } and this places every half + keyframes.
 * `groups` is [{ kind:'media', mediaText, rules:[{selectors, body}] } |
 * { kind:'rule', selectors, body } | { kind:'keyframes', name, text } |
 * { kind:'raw', text }] in source order.
 */
function assemblePartition(groups, drawerClassSet) {
  const hostParts = [];
  const drawerParts = [];
  const rulesMeta = [];
  const keyframes = [];
  const hostBodies = [];
  const drawerBodies = [];

  const placeRule = (selectors, body, edge, keptRules, drawerRules) => {
    if (selectors.some(selectorTargetsFragmentMarkers)) {
      drawerRules.push(`${selectors.join(', ')} ${body}`);
      drawerBodies.push(body);
      return;
    }
    if (!edge) {
      keptRules.push(`${selectors.join(', ')} ${body}`);
      hostBodies.push(body);
      return;
    }
    const { drawer, host } = partitionSelectors(selectors, drawerClassSet);
    if (host.length) {
      keptRules.push(`${host.join(', ')} ${body}`);
      hostBodies.push(body);
    }
    if (drawer.length) {
      drawerRules.push(`${drawer.join(', ')} ${body}`);
      drawerBodies.push(body);
    }
  };

  for (const group of groups) {
    if (group.kind === 'keyframes') {
      keyframes.push(group);
      continue;
    }
    if (group.kind === 'raw') {
      hostParts.push(group.text);
      continue;
    }
    if (group.kind === 'rule') {
      const keptRules = [];
      const drawerRules = [];
      placeRule(group.selectors, group.body, null, keptRules, drawerRules);
      hostParts.push(...keptRules);
      drawerParts.push(...drawerRules);
      continue;
    }
    // media group
    const edge = parseMaxWidthEdge(group.mediaText);
    const keptRules = [];
    const drawerRules = [];
    for (const rule of group.rules) {
      if (rule.raw != null) {
        keptRules.push(rule.raw);
        continue;
      }
      if (edge) {
        rulesMeta.push({
          mediaText: group.mediaText,
          selectors: rule.selectors,
          toggleShown: toggleShownIn(rule.selectors, rule.body)
        });
      }
      placeRule(rule.selectors, rule.body, edge, keptRules, drawerRules);
    }
    if (keptRules.length) hostParts.push(`@media ${group.mediaText} {\n${keptRules.join('\n')}\n}`);
    if (drawerRules.length) {
      drawerParts.push(`@media ${group.mediaText} {\n${drawerRules.join('\n')}\n}`);
    }
  }

  const hostAnim = animationNames(hostBodies.join('\n'));
  const drawerAnim = animationNames(drawerBodies.join('\n'));
  for (const kf of keyframes) {
    const inHost = kf.name && hostAnim.has(kf.name);
    const inDrawer = kf.name && drawerAnim.has(kf.name);
    if (inHost || !inDrawer) hostParts.push(kf.text);
    if (inDrawer) drawerParts.push(kf.text);
  }
  return {
    hostCss: hostParts.join('\n'),
    drawerCss: drawerParts.join('\n'),
    rulesMeta,
    parseFailed: false
  };
}

const PARSE_FAILED = (css) => ({
  hostCss: css || '',
  drawerCss: '',
  rulesMeta: [],
  parseFailed: true
});

// ── css-tree engine (Node: tests + tooling; the module is injected) ──────

export function createCssTreeCssEngine(csstree) {
  const gen = (node) => csstree.generate(node);

  function groupsOf(css) {
    const ast = csstree.parse(css || '', { positions: false });
    const groups = [];
    ast.children.forEach((node) => {
      if (
        node.type === 'Atrule' &&
        (node.name === 'keyframes' || node.name === '-webkit-keyframes')
      ) {
        groups.push({
          kind: 'keyframes',
          name: node.prelude ? gen(node.prelude) : '',
          text: gen(node)
        });
        return;
      }
      if (node.type === 'Atrule' && node.name === 'media' && node.prelude && node.block) {
        const rules = [];
        node.block.children.forEach((child) => {
          if (child.type !== 'Rule' || !child.prelude || child.prelude.type !== 'SelectorList') {
            rules.push({ raw: gen(child) });
            return;
          }
          const selectors = [];
          child.prelude.children.forEach((sel) => selectors.push(gen(sel)));
          rules.push({ selectors, body: gen(child.block) });
        });
        groups.push({ kind: 'media', mediaText: gen(node.prelude), rules });
        return;
      }
      if (node.type === 'Rule' && node.prelude?.type === 'SelectorList') {
        const selectors = [];
        node.prelude.children.forEach((sel) => selectors.push(gen(sel)));
        groups.push({ kind: 'rule', selectors, body: gen(node.block) });
        return;
      }
      groups.push({ kind: 'raw', text: gen(node) });
    });
    return groups;
  }

  return {
    readRules: createCssTreeRuleReader(csstree),
    partition(css, drawerClassSet) {
      let groups;
      try {
        groups = groupsOf(css);
      } catch {
        return PARSE_FAILED(css);
      }
      return assemblePartition(groups, drawerClassSet);
    },
    rewriteScopeAttr(css, sectionId) {
      if (!sectionId) return css || '';
      let ast;
      try {
        ast = csstree.parse(css || '', { positions: false });
      } catch {
        return css || '';
      }
      csstree.walk(ast, {
        visit: 'AttributeSelector',
        enter(node) {
          if (node.name?.name === 'data-pb-section' && node.value) {
            node.value = { type: 'String', value: sectionId };
          }
        }
      });
      return csstree.generate(ast);
    },
    rewriteMaxWidthEdges(css, fromEdge, toEdge) {
      let ast;
      try {
        ast = csstree.parse(css || '', { positions: false });
      } catch {
        return { css: css || '', rewritten: 0, untouched: [] };
      }
      let rewritten = 0;
      const untouched = [];
      const parts = [];
      ast.children.forEach((node) => {
        if (node.type === 'Atrule' && node.name === 'media' && node.prelude && node.block) {
          let mediaText = csstree.generate(node.prelude);
          const edge = parseMaxWidthEdge(mediaText);
          if (edge && sameEdge(edge, fromEdge)) {
            mediaText = mediaText.replace(edge.raw, toEdge.raw);
            rewritten++;
          } else if (edge) {
            untouched.push(mediaText);
          }
          parts.push(`@media ${mediaText} ${csstree.generate(node.block)}`);
          return;
        }
        parts.push(csstree.generate(node));
      });
      return { css: parts.join('\n'), rewritten, untouched };
    }
  };
}

// ── CSSOM engine (browser) ───────────────────────────────────────────────

function cssomParse(css) {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css || '');
    return Array.from(sheet.cssRules);
  } catch {
    return null;
  }
}

function cssomSelectors(rule) {
  return splitSelectors(rule.selectorText).map((s) => s.trim());
}

/**
 * Rewrite [data-pb-section="…"] values inside one already-split selector.
 * String surgery on a selector CSSOM has already parsed out — not CSS parsing.
 */
function rewriteScopeInSelector(selector, sectionId) {
  const needle = '[data-pb-section=';
  let out = '';
  let i = 0;
  for (;;) {
    const at = selector.indexOf(needle, i);
    if (at === -1) {
      out += selector.slice(i);
      return out;
    }
    const valueStart = at + needle.length;
    const quote = selector[valueStart];
    let valueEnd; // index of the first char AFTER the value (the closing ])
    if (quote === '"' || quote === "'") {
      const close = selector.indexOf(quote, valueStart + 1);
      if (close === -1) {
        out += selector.slice(i);
        return out;
      }
      valueEnd = close + 1;
    } else {
      const close = selector.indexOf(']', valueStart);
      if (close === -1) {
        out += selector.slice(i);
        return out;
      }
      valueEnd = close;
    }
    out += selector.slice(i, valueStart) + `"${sectionId}"`;
    i = valueEnd;
  }
}

export function createCssomCssEngine() {
  function groupsOf(css) {
    const rules = cssomParse(css);
    if (!rules) return null;
    const groups = [];
    for (const rule of rules) {
      if (typeof CSSKeyframesRule !== 'undefined' && rule instanceof CSSKeyframesRule) {
        groups.push({ kind: 'keyframes', name: rule.name || '', text: rule.cssText });
        continue;
      }
      if (typeof CSSMediaRule !== 'undefined' && rule instanceof CSSMediaRule) {
        const inner = [];
        for (const child of rule.cssRules) {
          if (child instanceof CSSStyleRule) {
            inner.push({
              selectors: cssomSelectors(child),
              body: `{ ${child.style.cssText} }`
            });
          } else {
            inner.push({ raw: child.cssText });
          }
        }
        groups.push({ kind: 'media', mediaText: rule.conditionText, rules: inner });
        continue;
      }
      if (rule instanceof CSSStyleRule) {
        groups.push({
          kind: 'rule',
          selectors: cssomSelectors(rule),
          body: `{ ${rule.style.cssText} }`
        });
        continue;
      }
      groups.push({ kind: 'raw', text: rule.cssText });
    }
    return groups;
  }

  return {
    readRules: createCssomRuleReader(),
    partition(css, drawerClassSet) {
      const groups = groupsOf(css);
      if (!groups) return PARSE_FAILED(css);
      return assemblePartition(groups, drawerClassSet);
    },
    rewriteScopeAttr(css, sectionId) {
      if (!sectionId) return css || '';
      const groups = groupsOf(css);
      if (!groups) return css || '';
      const rewriteGroup = (group) => {
        if (group.kind === 'media') {
          const inner = group.rules.map((r) =>
            r.raw != null
              ? r.raw
              : `${r.selectors.map((s) => rewriteScopeInSelector(s, sectionId)).join(', ')} ${r.body}`
          );
          return `@media ${group.mediaText} {\n${inner.join('\n')}\n}`;
        }
        if (group.kind === 'rule') {
          return `${group.selectors.map((s) => rewriteScopeInSelector(s, sectionId)).join(', ')} ${group.body}`;
        }
        return group.text;
      };
      return groups.map(rewriteGroup).join('\n');
    },
    rewriteMaxWidthEdges(css, fromEdge, toEdge) {
      const groups = groupsOf(css);
      if (!groups) return { css: css || '', rewritten: 0, untouched: [] };
      let rewritten = 0;
      const untouched = [];
      const parts = groups.map((group) => {
        if (group.kind !== 'media') {
          if (group.kind === 'rule') return `${group.selectors.join(', ')} ${group.body}`;
          return group.text;
        }
        let mediaText = group.mediaText;
        const edge = parseMaxWidthEdge(mediaText);
        if (edge && sameEdge(edge, fromEdge)) {
          mediaText = mediaText.replace(edge.raw, toEdge.raw);
          rewritten++;
        } else if (edge) {
          untouched.push(mediaText);
        }
        const inner = group.rules.map((r) =>
          r.raw != null ? r.raw : `${r.selectors.join(', ')} ${r.body}`
        );
        return `@media ${mediaText} {\n${inner.join('\n')}\n}`;
      });
      return { css: parts.join('\n'), rewritten, untouched };
    }
  };
}

// ── merge + revert (D5) ──────────────────────────────────────────────────

function refuse(reason, reasons, report) {
  return { ok: false, reason, reasons: reasons || [reason], report };
}

/** The desktop-safety guardrail (D4): fragment chrome/deco never leaks wide. */
function desktopGuardRule(sectionId, edge) {
  if (!edge) return '';
  const min = parseFloat((edge.rem + 0.0625).toFixed(4));
  const scope = sectionId ? `[data-pb-section="${sectionId}"] ` : '';
  return (
    `@media (min-width: ${min}rem) {\n` +
    `${scope}[${MARKER_ATTRS.deco}], ${scope}[${MARKER_ATTRS.chrome}] { display: none !important; }\n` +
    `}`
  );
}

/**
 * Pure merge: `nav` is the shared-navigation instance ({html, css, sectionId,
 * mobileMenu?, …}), `fragment` a mobile-menu catalog JSON. Returns
 * `{ ok, nextNav: {html, css, mobileMenu}, report }` or a refusal with
 * machine-readable reasons (eligibility slugs feed ELIGIBILITY_REASON_COPY).
 */
export function mergeMobileMenu(nav, fragment, cssEngine, { theme = null } = {}) {
  const report = { fragmentKey: fragment?.catalogKey || null, warnings: [], edgeRewrites: 0 };
  if (!fragment || fragment.fragmentContractVersion !== FRAGMENT_CONTRACT_VERSION) {
    return refuse('unsupported-fragment', null, report);
  }
  const reserved = reservedEditableIds(nav?.editableIdRepairs);
  if (reserved.size) {
    const incoming = parseHTMLFragment(
      [fragment.html, fragment.itemTemplate, fragment.dropdownTemplate].filter(Boolean).join('')
    ).container;
    if (
      [...incoming.querySelectorAll('[data-pb-eid]')].some((element) =>
        reserved.has(element.getAttribute('data-pb-eid'))
      )
    ) {
      return refuse(
        'identity-repair-conflict',
        ['The new menu reuses an identity reserved by an earlier element repair.'],
        report
      );
    }
  }
  const { container, doc } = parseHTMLFragment(nav?.html || '');
  const navRoot = container.firstElementChild;
  const eligibility = checkNavEligibility(navRoot);
  if (!eligibility.eligible) return refuse('ineligible-host', eligibility.reasons, report);

  const wrapper = navRoot.querySelector('.cs-ul-wrapper');
  const hostList = wrapper.querySelector('ul.cs-ul');
  const drawerClassSet = collectDrawerClassSet(navRoot);

  const partition = cssEngine.partition(nav.css || '', drawerClassSet);
  if (partition.parseFailed) return refuse('css-parse-failed', null, report);
  const hostEdge = detectMobileEdge(partition.rulesMeta);

  // First apply captures the stock snapshot; later applies carry it (D5).
  const carried = nav.mobileMenu?.stockSnapshot;
  const stockSnapshot =
    carried && typeof carried.drawerHtml === 'string' && typeof carried.drawerCss === 'string'
      ? { ...carried }
      : {
          drawerHtml: wrapper.outerHTML,
          drawerCss: partition.drawerCss,
          // The HOST half is snapshotted too, and revert needs it: a fragment
          // whose chrome shares a class with host markup OUTSIDE the drawer
          // (`.cs-button-solid` is stock CodeStitch, and casablanca's drawer
          // CTAs use it) widens revert's drawer class set, so a host rule that
          // classified host at apply reclassifies DRAWER at revert. It then
          // lands in the recomputed drawer half, which revert discards in
          // favour of drawerCss — and drawerCss never held it. Observed on
          // uicompass: `.cs-button-solid,.cs-remove-flex{display:none}`
          // vanished on apply->revert and the mobile CTA drew over the logo.
          hostCss: partition.hostCss,
          sourceHash: typeof nav.sourceHash === 'string' ? nav.sourceHash : null,
          provenancePath: typeof nav.provenancePath === 'string' ? nav.provenancePath : null
        };

  const model = buildNavLinkModel(hostList);

  const holder = doc.createElement('div');
  holder.innerHTML = fragment.html || '';
  const fragWrapper = holder.firstElementChild;
  if (!fragWrapper || !fragWrapper.classList.contains('cs-ul-wrapper')) {
    return refuse('malformed-fragment', null, report);
  }
  const listSlot = fragWrapper.querySelector(`[${MARKER_ATTRS.slot}="list"]`);
  if (!listSlot) return refuse('fragment-missing-slot', null, report);
  fragWrapper.setAttribute(MARKER_ATTRS.fragment, fragment.catalogKey || '');

  // Before anything is grafted onto the host: strip the donor's contact data.
  report.chromeLinksRewritten = sanitizeChromeLinks(fragWrapper);
  if (report.chromeLinksRewritten) report.warnings.push('chrome-links-rebound');

  if (fragment.itemTemplate) {
    listSlot.replaceWith(renderMenuList(hostList, model, fragment, doc));
  } else {
    // Template-less (stock-chrome) fragment: the host's real list node moves
    // verbatim into the slot — same nodes, same eids, same labels.
    listSlot.replaceWith(hostList);
  }
  wrapper.replaceWith(fragWrapper);

  let fragCss = cssEngine.rewriteScopeAttr(fragment.css || '', nav.sectionId || '');
  if (!nav.sectionId) report.warnings.push('host-section-id-missing');
  const fragEdge = fragment.mobileEdge
    ? parseMaxWidthEdge(`(max-width: ${fragment.mobileEdge})`)
    : null;
  if (hostEdge && fragEdge && !sameEdge(hostEdge, fragEdge)) {
    const rewrite = cssEngine.rewriteMaxWidthEdges(fragCss, fragEdge, hostEdge);
    fragCss = rewrite.css;
    report.edgeRewrites = rewrite.rewritten;
    for (const params of rewrite.untouched) {
      report.warnings.push(`media-left-untouched: ${params}`);
    }
  } else if (!hostEdge) {
    report.warnings.push('host-edge-undetected');
  }

  // Theme-var adoption has no contrast guarantee: casablanca's drop links are
  // `var(--primary)` on a `var(--primaryDark)` panel, and --primaryDark is
  // DERIVED from --primary by darkening, so the pair reads 8.70:1 on its own
  // cream primary and 2.61:1 on uicompass's dark green — invisible text. The
  // repair is appended AFTER fragCss so it wins position, and it echoes the
  // losing rule's own selector so it also wins specificity.
  let contrastCss = '';
  if (theme && typeof cssEngine.readRules === 'function') {
    const contrast = deriveContrastOverrides(fragCss, theme, {
      readRules: cssEngine.readRules,
      sourceTheme: fragment.sourceTheme || null
    });
    contrastCss = contrast.css;
    report.contrastFindings = contrast.findings;
    if (contrastCss) report.warnings.push('drawer-contrast-repaired');
    if (!fragment.sourceTheme) report.warnings.push('fragment-has-no-source-theme');
  }

  const guard = desktopGuardRule(nav.sectionId || '', hostEdge || fragEdge);
  const nextCss = [partition.hostCss, fragCss, contrastCss, guard].filter(Boolean).join('\n');
  // Surviving eids are collected BEFORE re-stamping: stampEditableIds reuses
  // freed id numbers, so "eid still present in the final HTML" is NOT proof an
  // override's element survived — pruning must intersect with this set.
  report.survivingEids = [...container.querySelectorAll('[data-pb-eid]')].map((el) =>
    el.getAttribute('data-pb-eid')
  );
  const { html: stampedHtml } = stampEditableIds(container.innerHTML, {
    editableIdRepairs: nav.editableIdRepairs
  });

  return {
    ok: true,
    nextNav: {
      html: stampedHtml,
      css: nextCss,
      mobileMenu: {
        fragmentKey: fragment.catalogKey,
        fragmentContractVersion: FRAGMENT_CONTRACT_VERSION,
        fragmentSourceHash: typeof fragment.sourceHash === 'string' ? fragment.sourceHash : null,
        stockSnapshot
      }
    },
    report
  };
}

/**
 * Pure revert-to-stock: restores the snapshot bytes captured at first apply.
 * Never re-fetches by catalogKey (a newer catalog entry is NOT this nav's
 * stock). Returns the same shape as mergeMobileMenu, with mobileMenu: null.
 */
export function revertMobileMenu(nav, cssEngine) {
  const report = { warnings: [] };
  const snapshot = nav?.mobileMenu?.stockSnapshot;
  if (!snapshot || typeof snapshot.drawerHtml !== 'string') {
    return refuse('no-snapshot', null, report);
  }
  const { container, doc } = parseHTMLFragment(nav.html || '');
  const navRoot = container.firstElementChild;
  const wrapper = navRoot?.querySelector('.cs-ul-wrapper');
  if (!wrapper) return refuse('no-wrapper', null, report);

  // Drawer classes must cover BOTH the fragment chrome being stripped and the
  // stock chrome coming back, so collect before and after the restore.
  const beforeSet = collectDrawerClassSet(navRoot);
  const holder = doc.createElement('div');
  holder.innerHTML = snapshot.drawerHtml;
  const stockWrapper = holder.firstElementChild;
  if (!stockWrapper) return refuse('malformed-snapshot', null, report);
  wrapper.replaceWith(stockWrapper);
  const drawerClassSet = collectDrawerClassSet(navRoot);
  for (const cls of beforeSet) drawerClassSet.add(cls);

  const partition = cssEngine.partition(nav.css || '', drawerClassSet);
  if (partition.parseFailed) return refuse('css-parse-failed', null, report);

  // Heal the reclassification loss described where hostCss is captured: run the
  // ORIGINAL host half through the same widened set and take what it now calls
  // drawer — exactly the rules this revert would otherwise drop on the floor.
  // Surviving rules keep their CURRENT bytes, so this restores only what is
  // actually missing. Snapshots taken before hostCss existed skip the heal and
  // say so rather than silently reverting lossily.
  let recoveredHostCss = '';
  if (typeof snapshot.hostCss === 'string') {
    const original = cssEngine.partition(snapshot.hostCss, drawerClassSet);
    if (original.parseFailed) {
      report.warnings.push('host-recovery-parse-failed');
    } else {
      recoveredHostCss = original.drawerCss || '';
      if (recoveredHostCss) report.warnings.push('host-rules-recovered');
    }
  } else {
    report.warnings.push('legacy-snapshot-no-host-css');
  }

  const nextCss = [partition.hostCss, recoveredHostCss, snapshot.drawerCss]
    .filter(Boolean)
    .join('\n');
  report.survivingEids = [...container.querySelectorAll('[data-pb-eid]')].map((el) =>
    el.getAttribute('data-pb-eid')
  );
  const { html: stampedHtml } = stampEditableIds(container.innerHTML, {
    editableIdRepairs: nav.editableIdRepairs
  });
  return { ok: true, nextNav: { html: stampedHtml, css: nextCss, mobileMenu: null }, report };
}

/**
 * Prune element overrides after a merge/revert: keep only entries whose eid
 * SURVIVED the DOM operation (report.survivingEids — final-HTML presence is
 * not enough, freed eid numbers get reused by re-stamping), then run the
 * standard orphan/picture prune against the final HTML. Pure; used by
 * section-manager's apply/reset/swap callers.
 */
export function pruneOverridesForMerge(overrides, html, survivingEids) {
  if (!overrides || typeof overrides !== 'object') return overrides ?? null;
  const surviving = new Set(survivingEids || []);
  const pruneBucket = (elements) => {
    if (!elements || typeof elements !== 'object') return null;
    const kept = {};
    for (const [eid, props] of Object.entries(elements)) {
      if (surviving.has(eid)) kept[eid] = props;
    }
    return pruneOrphanOverrides(html, kept);
  };
  const next = structuredClone(overrides);
  const prunedTop = pruneBucket(next.elements);
  if (prunedTop) next.elements = prunedTop;
  else delete next.elements;
  if (next.responsive && typeof next.responsive === 'object') {
    for (const viewport of Object.keys(next.responsive)) {
      const bucket = next.responsive[viewport];
      if (!bucket || typeof bucket !== 'object') continue;
      const pruned = pruneBucket(bucket.elements);
      if (pruned) bucket.elements = pruned;
      else delete bucket.elements;
      if (!Object.keys(bucket).length) delete next.responsive[viewport];
    }
    if (!Object.keys(next.responsive).length) delete next.responsive;
  }
  return Object.keys(next).length ? next : null;
}
